import { useState, useEffect } from 'react';
import { Category } from '../types';
import { categoryApi } from '../services/api';

export const useCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryApi.getAll();
      const sortedCategories = data.sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
        }
        return a.name.localeCompare(b.name);
      });
      setCategories(sortedCategories);
      setError(null);
    } catch (err) {
      setError('Failed to fetch categories');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const refreshCategories = () => {
    fetchCategories();
  };

  const addCategory = async (category: Omit<Category, 'id' | 'createdDate' | 'updatedDate'>) => {
    try {
      const newCategory = await categoryApi.create(category);
      const updatedCategories = [...categories, newCategory].sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
        }
        return a.name.localeCompare(b.name);
      });
      setCategories(updatedCategories);
      return newCategory;
    } catch (err) {
      console.error('Failed to add category:', err);
      throw err;
    }
  };

  const updateCategory = async (id: string, category: Partial<Category>) => {
    try {
      const updatedCategory = await categoryApi.update(id, category);
      const updatedCategories = categories.map(c => c.id === id ? updatedCategory : c).sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
        }
        return a.name.localeCompare(b.name);
      });
      setCategories(updatedCategories);
      return updatedCategory;
    } catch (err) {
      console.error('Failed to update category:', err);
      throw err;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      await categoryApi.delete(id);
      setCategories(categories.filter(c => c.id !== id));
      if (selectedCategory === id) {
        setSelectedCategory(null);
      }
    } catch (err) {
      console.error('Failed to delete category:', err);
      throw err;
    }
  };

  return {
    categories,
    loading,
    error,
    selectedCategory,
    setSelectedCategory,
    refreshCategories,
    addCategory,
    updateCategory,
    deleteCategory
  };
};

export default useCategories; 